import { JobAction } from "~/interface/Job"

// A job's `__items` is a denormalized copy of the cart lines that rides along on
// PAY / VOID / SET_COMMENTS / SET_DISCOUNT / SPLIT / MOVE cards. The drop-chain
// replay engine (JobchainV1_1) never reads it — it rebuilds tables from `json`
// (ADD) and `json2` (the {uniqueid: qty} map). So `__items` is pure wire +
// storage weight on every reload, and most of its fields are never read at all.
//
// These are the ONLY item fields any consumer actually reads off a *persisted*
// job. Everything else (dc1, toppingsAmount, extendedPrice, bill) is recomputed
// on the output table item or never read. Keep this list in sync if a consumer
// starts reading a new field:
//   - report.ts printVoidItems / VoidsPage / VoidItems / Dashboard  (reload, VOID jobs)
//   - job.to.voidItem / changePrice / openMenu / activity plugins    (live create)
//   - AdapterMobileHistoryV1.buildVoidItems                          (live create, VOID history)
const KEPT_ITEM_FIELDS = [
  "uuid", // matched against json2 by void readers + mobile void history
  "name", // every void reader + activity log message
  "quantity", // void readers, openMenu tracker, activity
  "price", // void readers, openMenu, changePrice, mobile history
  "discountedPrice", // VoidsPage void amount
  "subItemId", // printVoidItems, voidItem plugin, mobile history
  "comments", // mobile void-history rows
  "toppings", // mobile void-history rows + genItemLineKey
  "isOpenMenu", // openMenu plugin filter
  "openMenuName", // openMenu plugin payload
] as const

function pickKeptFields(item: any): any {
  const out: any = {}
  for (const key of KEPT_ITEM_FIELDS) {
    if (item?.[key] !== undefined) out[key] = item[key]
  }
  return out
}

/** Trim each line to only the fields a consumer reads (drops dc1, toppingsAmount,
 *  extendedPrice, bill). Rows are kept as-is. */
export function slimJobItems(items: any[] | undefined | null): any[] {
  if (!Array.isArray(items)) return []
  return items.map(pickKeptFields)
}

/**
 * Slim the `__items` a JobBuilder is about to persist, per job type:
 *  - PAY (700): replay rebuilds paid lines from `json2`, so the only thing that
 *    ever reads a PAY card's `__items` is the open-menu sales tracker
 *    (job.to.openMenu.plugin), which filters to `isOpenMenu` rows. Drop every
 *    other row — for a shop that doesn't use open-menu items this leaves `[]`,
 *    which JobBuilder.build() then omits entirely (~28% of the payload gone).
 *  - everything else: keep the rows (VOID needs them for the void report +
 *    mobile history; comment/discount/split/move for the activity log), just
 *    trim the unused fields.
 */
export function slimJobItemsForPersist(type: any, items: any[] | undefined | null): any[] {
  if (!Array.isArray(items)) return []
  const rows = type === JobAction.PAY ? items.filter((it) => it?.isOpenMenu === true) : items
  return slimJobItems(rows)
}
